/**
 * ORG-040: PermissionGate — declarative permission fence.
 *
 * Usage:
 *   <PermissionGate allowed={perms.canManageTeam}>
 *     <SensitiveWidget />
 *   </PermissionGate>
 *
 * When `allowed` is false it renders a clean locked-state instead of
 * crashing or exposing the children.
 */
import { Lock } from 'lucide-react';
import type { ReactNode } from 'react';

interface PermissionGateProps {
  /** Whether the current user has permission. */
  allowed: boolean;
  /** Children to render when allowed. */
  children: ReactNode;
  /**
   * Optional custom locked UI.
   * Defaults to a centred lock card.
   */
  fallback?: ReactNode;
  /** Short label for the locked state (default: "Restricted"). */
  label?: string;
  /** Extra detail shown below the label. */
  detail?: string;
}

function DefaultLocked({ label, detail }: { label: string; detail?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-800 bg-gray-900/40 px-6 py-10 text-center">
      <Lock className="w-8 h-8 text-gray-600" />
      <p className="text-sm font-medium text-gray-400">{label}</p>
      {detail && <p className="text-xs text-gray-600 max-w-xs">{detail}</p>}
    </div>
  );
}

export function PermissionGate({
  allowed,
  children,
  fallback,
  label = 'Restricted',
  detail = 'You do not have permission to view this section.',
}: PermissionGateProps) {
  if (allowed) return <>{children}</>;
  if (fallback !== undefined) return <>{fallback}</>;
  return <DefaultLocked label={label} detail={detail} />;
}
